import React from 'react';
import { Link } from 'react-router-dom';

interface BrandLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showSubtitle?: boolean;
  iconOnly?: boolean;
  linkTo?: string;
  theme?: 'light' | 'dark' | 'glass';
  className?: string;
  onClick?: () => void;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 'md',
  showSubtitle = true,
  iconOnly = false,
  linkTo = '/dashboard',
  theme = 'glass',
  className = '',
  onClick
}) => {
  const headerText = localStorage.getItem('app_header_text') || 'Gestão Fácil';
  const savedSubtitle = localStorage.getItem('app_header_subtitle');
  const headerSubtitle = (savedSubtitle && !['Engmont ERP', 'ENGMONT ERP', 'Painel Web'].includes(savedSubtitle))
    ? savedSubtitle
    : 'By Alonedev.com';
  const logoUrl = localStorage.getItem('report_logo_url');

  const sizeClasses = {
    sm: {
      iconBox: 'w-8 h-8 rounded-lg',
      svg: 'w-4 h-4',
      title: 'text-sm font-bold',
      sub: 'text-[9px]',
      gap: 'gap-2.5',
    },
    md: {
      iconBox: 'w-9 h-9 rounded-xl',
      svg: 'w-5 h-5',
      title: 'text-base font-bold',
      sub: 'text-[10px]',
      gap: 'gap-2.5',
    },
    lg: {
      iconBox: 'w-12 h-12 rounded-2xl',
      svg: 'w-6 h-6',
      title: 'text-xl sm:text-2xl font-bold',
      sub: 'text-xs',
      gap: 'gap-3',
    }
  }[size];

  const themeStyles = {
    glass: {
      text: 'text-white',
      sub: 'text-purple-200/90 dark:text-purple-300/80',
    },
    light: {
      text: 'text-slate-900',
      sub: 'text-slate-500',
    },
    dark: {
      text: 'text-white',
      sub: 'text-slate-400',
    }
  }[theme];

  // Minimalist, Clean Vector Icon
  const iconElement = (
    <div className={`relative shrink-0 transition-transform duration-200 group-hover:scale-105 group-active:scale-95`}>
      {logoUrl ? (
        <div className={`${sizeClasses.iconBox} overflow-hidden bg-white shadow-sm p-1 border border-white/20 flex items-center justify-center`}>
          <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
        </div>
      ) : (
        <div className={`${sizeClasses.iconBox} bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center shadow-md shadow-purple-900/30 text-white`}>
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg" 
            className={`${sizeClasses.svg} text-white`}
          >
            {/* Minimalist modern financial growth bars and trajectory line */}
            <path d="M4 18V13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M9 18V10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M14 18V7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M4 12L9 8L14 5L20 2M20 2H15M20 2V7" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );

  const content = (
    <div className={`flex items-center ${iconOnly ? 'justify-center' : sizeClasses.gap} group cursor-pointer select-none ${className}`} onClick={onClick}>
      {iconElement}

      {/* Typography / System Name (Hidden when iconOnly) - Minimalist without ERP */}
      {!iconOnly && (
        <div className="flex flex-col leading-tight min-w-0">
          <span className={`tracking-tight ${sizeClasses.title} ${themeStyles.text} transition-colors group-hover:text-purple-200 truncate`}>
            {headerText}
          </span>
          {showSubtitle && (
            <span className={`font-medium tracking-normal ${sizeClasses.sub} ${themeStyles.sub} mt-0.5 truncate flex items-center gap-1.5`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
              {headerSubtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link 
        to={linkTo} 
        className="inline-flex items-center focus:outline-none rounded-xl" 
        title={`${headerText} - Ir para Despesas`}
      >
        {content}
      </Link>
    );
  }

  return content;
};
export default BrandLogo;
